import { getGlobalPhoneArea } from './countries';
import axios from 'axios';
import i18n from '@pro/common/i18n/i18n';
import moment from 'moment';
import cookie from 'js-cookie';
import Bowser from "bowser";
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { INSTALLER_SECOND_PERMISSION } from '@/Common/libs/constant.js';
import isMobile from '@pro/common/utils/isMobile';
import getPasswordStrength from '@pro/common/utils/getPasswordStrength';
import getPasswordStrongStrength from '@pro/common/utils/getPasswordStrongStrength';
import getSameLevelMaxZindex from '@pro/common/utils/getSameLevelMaxZindex';
import getCookiesOption from '@pro/common/utils/getCookiesOption';
import Storage from '@/Common/libs/storage';

/**
 * 检查工具方法
 */
const basicCheck = {
  /**
   * 判断浏览器是否是IE
   * @returns {boolean}
   */
  IETester(userAgent) {
    let UA = userAgent || navigator.userAgent;
    if ((/msie/i).test(UA)) {
      return UA.match(/msie (\d+\.\d+)/i)[1];
    } else if (~UA.toLowerCase().indexOf('trident') && ~UA.indexOf('rv')) {
      return UA.match(/rv:(\d+\.\d+)/)[1];
    }
    return false;
  },
  /**
   * @synopsis 判断是否为macos
   * @returns {boolean} 是否为macos
   */
  isMacOS() {
    let szPlatform = navigator.platform;
    return 'MacIntel' === szPlatform;
  },
  /**
   * @synopsis 判断是否为ios
   * @returns {boolean} 是否为ios系统
   */
  isIOS() {
    if (navigator.userAgent.match(/\((i|M)[^;]+;( U;)? (CPU|Intel).+Mac OS X/)) {
      return true;
    }
    return false;
  },

  /**
   * @synopsis 判断是否为移动端
   * @returns {boolean} 是否为移动端
   */
  isMobile,

  /**
   * 判断是否是safari浏览器
   * @returns {boolean}
   */
  isSafari() {
    let userAgent = navigator.userAgent;
    return (/Safari/).test(userAgent) && !(/Chrome/).test(userAgent);
  },
  /**
   * 获取浏览器语言的方法
   * @returns {string} 根语言，除中文外，其他语言的方言不考虑，仅采用前两位
   */
  getBrowserLanguage() {
    let language = (navigator.language || navigator.browserLanguage).toLowerCase();
    language = language.replace('-', '_');
    let rootLanguage = language.substring(0, 2);
    if ('zh' === rootLanguage || 'pt' === rootLanguage) {
      rootLanguage = language;
    }
    return rootLanguage;
  },

  /**
   * 根据语言获取编码类型
   * @param lng {String} 语言
   * @returns {string} 编码格式
   */
  getEncoding(lng) {
    let encodeTable = {
      bg: 'iso-8859-5',
      cs: 'iso-8859-2',
      da: 'iso-8859-1',
      de: 'iso-8859-1',
      es: 'iso-8859-1',
      fr: 'iso-8859-15',
      hu: 'iso-8859-2',
      it: 'iso-8859-1',
      ja: 'shift-jis',
      nl: 'iso-8859-1',
      pl: 'iso-8859-2',
      pt: 'iso-8859-1',
      ru: 'iso-8859-5',
      th: 'iso-8859-11',
      tr: 'iso-8859-3',
      zh_cn: 'gb2312',
      zh: 'gb2312',
      zh_tw: 'big5',
      ko: 'EUC-KR',
      fi: 'iso-8859-15',
      vi: 'Windows-1258',
      et: 'iso-8859-4',
      el: 'iso-8859-7',
      sk: 'iso-8859-2',
      ro: 'iso-8859-2',
      sv: 'iso-8859-10',
      nb: 'iso-8859-10',
      hr: 'iso-8859-2',
      sl: 'iso-8859-2',
      sr: 'iso-8859-2',
      lv: 'iso-8859-5',
      lt: 'iso-8859-4',
      'pt-BR': 'iso-8859-15',
      kk: '8859-5',
      ky: '8859-5',
      ar: 'iso-8859-6',
      fa: 'iso-8859-6',
      he: 'iso-8859-8',
      uz: '8859-9'
    };
    lng = lng || basicCheck.getBrowserLanguage();
    return encodeTable[lng] || 'utf-8';
  },

  encoding() {
    return basicCheck.getEncoding(basicCheck.getBrowserLanguage());
  },

  getCsvSeparator() {
    //0:逗号 1：分号
    const separatorMap = {
      bg: 1,
      cs: 1,
      da: 1,
      de: 1,
      es: 1,
      fr: 1,
      hu: 1,
      it: 1,
      ja: 0,
      nl: 1,
      pl: 1,
      pt: 1,
      ru: 1,
      th: 1,
      tr: 1,
      zh_cn: 0,
      zh: 0,
      zh_tw: 0,
      ko: 0,
      fi: 1,
      vi: 1,
      et: 1,
      el: 1,
      sk: 1,
      ro: 1,
      sv: 1,
      nb: 1,
      hr: 1,
      sl: 1,
      sr: 1,
      lv: 1,
      lt: 1,
      'pt-BR': 1,
      kk: 1,
      ky: 1,
      ar: 1,
      fa: 1,
      he: 1,
      uz: 1,
      ur: 1,
      bn: 1
    };
    const language = this.getBrowserLanguage();
    return separatorMap[language] || 0;
  },
  isFirefox() {
    return navigator.userAgent.indexOf('Firefox') > -1;
  }
};

/**
 * 获取密码强度的工具方法
 * @param username {String} 用户名
 * @param password {String} 密码
 * @returns {number} 密码强度
 */

const parseXmlFromFilePath = function (fileRoute) {
  return new Promise((resolve) => {
    let xmlDoc = null;
    if (document.implementation && document.implementation.createDocument) {
      let xmlhttp = new window.XMLHttpRequest();
      xmlhttp.onreadystatechange = function () {
        if (xmlhttp.readyState === 4) {
          if (xmlhttp.status === 200) {
            xmlDoc = xmlhttp.responseXML;
            resolve(xmlDoc);
          } else {
            resolve(null);
          }
        }
      };
      xmlhttp.open('GET', fileRoute, true);
      //timeout代码必须放在open之后send之前，否则IE无法识别
      xmlhttp.timeout = 20000;
      xmlhttp.ontimeout = function () {
        resolve(null);
      };
      xmlhttp.send(null);
    } else {
      resolve(null);
    }
  });
};

const formatPhoneNum = function (phoneNum, onlyPhoneNumber = false) {
  const isThree = (/^\d+-\d+-\d+$/).test(phoneNum);
  const isTwo = (/^\d+-\d+$/).test(phoneNum);
  if (!isThree && !isTwo) {
    return phoneNum;
  }
  const tmp = phoneNum.split('-');
  if (onlyPhoneNumber) {
    return tmp[tmp.length - 1];
  }
  return `+${tmp[tmp.length - 2]} ${tmp[tmp.length - 1]}`;
};

const formatCompanyPhoneNum = function (phoneNum) {
  try {
    const tmp = phoneNum.split('-');
    return tmp[tmp.length - 1];
  } catch (error) {
    return phoneNum;
  }
};

// 用于从服务端默认电话格式获取第三段电话号码，仅用于公司信息模块（因为第三方公司手机号格式不确定），其他模块请使用formatPhoneNum
const getPhoneNum = function (phoneNum) {
  if (phoneNum) {
    const arr = phoneNum.split('-');
    if (arr.length > 1) {
      return arr[arr.length - 1];
    }
    return phoneNum;
  }
  return '';
};

const getPhoneCountry = function (phoneNum) {
  let areaCode = '';
  let arr = [];
  if (phoneNum) {
    arr = phoneNum.split('-');
    if (arr.length > 1) {
      areaCode = arr[arr.length - 2];
    }
  }

  let countryIndex = cookie.get('country') && cookie.get('country') !== 'undefined' ? cookie.get('country') : '0';
  if (areaCode) {
    let countrys = getGlobalPhoneArea();
    areaCode = parseInt(areaCode, 10);
    let countryItem = countrys.find((c) => c.phoneAreaCode === areaCode) || {};
    countryIndex = countryItem.index || 0;
  }
  if (arr.length === 3) {
    countryIndex = arr[0] || countryIndex;
  }
  return parseInt(countryIndex, 10);
};

const joinPhoneNum = function (countryIndex, phoneNum) {
  const countryObj = _.find(getGlobalPhoneArea(), ['index', parseInt(countryIndex, 10)]);
  return `${countryIndex}-${countryObj.phoneAreaCode}-${phoneNum}`;
};

const capitalize = function ([first, ...rest], lowerRest = false) {
  return first.toUpperCase() + (lowerRest ? rest.join('').toLowerCase() : rest.join(''));
};

const base64Util = {
  // private property
  _keyStr: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',
  // public method for encoding
  encode: function (input) {
    let output = '';
    let chr1;
    let chr2;
    let chr3;
    let enc1;
    let enc2;
    let enc3;
    let enc4;
    let i = 0;
    input = this._utf8Encode(input);
    while (i < input.length) {
      chr1 = input.charCodeAt(i++);
      chr2 = input.charCodeAt(i++);
      chr3 = input.charCodeAt(i++);
      enc1 = chr1 >> 2;
      enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
      enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
      enc4 = chr3 & 63;
      if (isNaN(chr2)) {
        enc3 = enc4 = 64;
      } else if (isNaN(chr3)) {
        enc4 = 64;
      }
      output
        = output
        + this._keyStr.charAt(enc1)
        + this._keyStr.charAt(enc2)
        + this._keyStr.charAt(enc3)
        + this._keyStr.charAt(enc4);
    }
    return output;
  },
  // public method for decoding
  decode: function (input) {
    let output = '';
    let chr1;
    let chr2;
    let chr3;
    let enc1;
    let enc2;
    let enc3;
    let enc4;
    let i = 0;
    input = input.replace(/[^A-Za-z0-9+/=]/g, '');
    while (i < input.length) {
      enc1 = this._keyStr.indexOf(input.charAt(i++));
      enc2 = this._keyStr.indexOf(input.charAt(i++));
      enc3 = this._keyStr.indexOf(input.charAt(i++));
      enc4 = this._keyStr.indexOf(input.charAt(i++));
      chr1 = (enc1 << 2) | (enc2 >> 4);
      chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
      chr3 = ((enc3 & 3) << 6) | enc4;
      output = output + String.fromCharCode(chr1);
      if (enc3 !== 64) {
        output = output + String.fromCharCode(chr2);
      }
      if (enc4 !== 64) {
        output = output + String.fromCharCode(chr3);
      }
    }
    output = this._utf8Decode(output);
    return output;
  },
  // private method for UTF-8 encoding
  _utf8Encode: function (string) {
    string = string.replace(/\r\n/g, '\n');
    let utftext = '';
    for (let n = 0; n < string.length; n++) {
      let c = string.charCodeAt(n);
      if (c < 128) {
        utftext += String.fromCharCode(c);
      } else if (c > 127 && c < 2048) {
        utftext += String.fromCharCode((c >> 6) | 192);
        utftext += String.fromCharCode((c & 63) | 128);
      } else {
        utftext += String.fromCharCode((c >> 12) | 224);
        utftext += String.fromCharCode(((c >> 6) & 63) | 128);
        utftext += String.fromCharCode((c & 63) | 128);
      }
    }
    return utftext;
  },
  // private method for UTF-8 decoding
  _utf8Decode: function (utftext) {
    let string = '';
    let i = 0;
    let c = 0;
    let c3 = 0;
    let c2 = 0;
    while (i < utftext.length) {
      c = utftext.charCodeAt(i);
      if (c < 128) {
        string += String.fromCharCode(c);
        i++;
      } else if (c > 191 && c < 224) {
        c2 = utftext.charCodeAt(i + 1);
        string += String.fromCharCode(((c & 31) << 6) | (c2 & 63));
        i += 2;
      } else {
        c2 = utftext.charCodeAt(i + 1);
        c3 = utftext.charCodeAt(i + 2);
        string += String.fromCharCode(((c & 15) << 12) | ((c2 & 63) << 6) | (c3 & 63));
        i += 3;
      }
    }
    return string;
  }
};

//请求json文件
const getJson = function (url) {
  if (!url.startsWith('/')) {
    url = `/${url}`;
  }
  return new Promise((resolve, reject) => {
    axios({
      method: 'get',
      url: url,
      dataType: 'json',
      cache: false
    })
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

function copyToClipboard(text) {
  return new Promise((resolve) => {
    let transfer = document.createElement('input');
    document.body.appendChild(transfer);
    transfer.value = text;
    transfer.focus();
    transfer.select();
    if (document.execCommand('copy')) {
      document.execCommand('copy');
    }
    transfer.blur();
    document.body.removeChild(transfer);
    resolve();
  });
}

function removeDom(selector) {
  const collection = document.querySelectorAll(selector);
  for (let i = 0; i < collection.length; i++) {
    collection[i].parentElement.removeChild(collection[i]);
  }
}

function updatePermission(p) {
  let permission = [];
  let flag = 0;
  p.forEach((value) => {
    if (parseInt(value, 10) >= 0 && parseInt(value, 10) <= 3) {
      flag++;
    } else if (value !== '4' && value !== '9' && value !== '99') {
      permission.push(value);
    }
  });
  if (flag === 4) {
    permission.unshift('99');
  }
  return permission;
}

function updatePermissionName(p) {
  let permission = [];
  let flag = 0;
  if (p && p.length) {
    p.forEach((value) => {
      if (value === 'SITE:MANAGER' || value === 'SITE:INFO' || value === 'SITE:CREATE' || value === 'SITE:DELETE') {
        flag++;
      } else if (value !== 'SITE:ASSIGN' && value !== 'COMPANY:PACKAGE' && value !== 'SITE:SINGLE') {
        permission.push(value);
      }
    });
    if (flag === 4) {
      permission.unshift('SITE:SINGLE');
    }
  }
  return permission;
}

function transferToKey(p) {
  let permission = [];
  if (p && p.length) {
    p.forEach((value) => {
      switch (value) {
        case 'DEVICE:REMOTE_CONFIG':
          permission.push(INSTALLER_SECOND_PERMISSION.REMOTE_CONFIG);
          return;
        case 'DEVICE:VIDEO_PREVIEW':
          permission.push(INSTALLER_SECOND_PERMISSION.VIDEO_PREVIEW);
          return;
        case 'DEVICE:VIDEO_PLAYBACK':
          permission.push(INSTALLER_SECOND_PERMISSION.VIDEO_PLAYBACK);
          return;
        case 'DEVICE:AXPRO_DEPLOYMENT':
          permission.push(INSTALLER_SECOND_PERMISSION.AXPRO_DEPLOYMENT);
          return;
        case 'DEVICE:AXPRO_DISARM':
          permission.push(INSTALLER_SECOND_PERMISSION.AXPRO_DISARM);
          return;
        case 'DEVICE:AXPRO_BYPASS':
          permission.push(INSTALLER_SECOND_PERMISSION.AXPRO_BYPASS);
          return;
        case 'DEVICE:USER_KEYBOARD_PASSWORD':
          permission.push(INSTALLER_SECOND_PERMISSION.USER_KEYBOARD_PASSWORD);
          return;
        case 'BATCH_INVITE_HC_ACCOUNT':
          permission.push(INSTALLER_SECOND_PERMISSION.BATCH_INVITE_HC_ACCOUNT);
          break;
        default:
      }
    });
  }
  return permission;
}

function splitPermission(p) {
  let permission = [];
  if (p && p.length) {
    p.forEach((value) => {
      if (value === '99') {
        permission.push('0', '1', '2', '3', '9');
      } else if (value === '7') {
        permission.push('7', '9');
      } else {
        permission.push(value);
      }
    });
  }
  return permission;
}

/* 获取url参数 */
function getQueryString(name, params) {
  var reg = new RegExp(`(^|&|/?)${name}=([^&]*)(&|$)`, 'i');
  var search = window.location.href;
  if (params) {
    search = params;
  } else {
    if (search.indexOf('?') > -1) {
      search = search.substr(search.indexOf('?'));
    } else {
      search = '';
    }
    search = search.substr(1);
  }
  var r = search.match(reg); //获取url中"?"符后的字符串并正则匹配
  var context = '';
  if (r !== null) {
    context = r[2];
  }
  reg = null;
  r = null;
  return context === null || context === '' || context === 'undefined' ? '' : context;
}

/**
 * js实例组件并渲染到 body下方
 * @param instance  组件实例
 */
const createDom = (instance) => {
  instance.vm = instance.$mount();
  document.body.appendChild(instance.vm.$el);
  // return instance.$mount();
};

/**
 *
 * @param val 数值
 * @param symbols 货币类型
 * @param decimal 保留小数位
 * @returns {string|*}
 */
const formatMoneyNumber = (val, symbols, decimal = 2) => {
  if (!val) {
    return val;
  }
  const decimalNum = parseFloat(`${val}`).toFixed(decimal);
  const currencySymbol = symbols ? `${symbols.toUpperCase()} ` : '';
  return currencySymbol + (`${decimalNum}`).replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,');
};

const getURLByCountryIndex = (index) => {
  if (process.env.appmode !== 'NORMAL') {
    return window.location.origin;
  }
  if (window.REGION_EU.indexOf(index) !== -1) {
    return window.REGION_HREF.EU;
  }
  if (window.REGION_SA.indexOf(index) !== -1) {
    return window.REGION_HREF.SA;
  }
  if (window.REGION_SGP.indexOf(index) !== -1) {
    return window.REGION_HREF.SGP;
  }
  if (window.REGION_US.indexOf(index) !== -1) {
    return window.REGION_HREF.US;
  }
  if (window.REGION_RU.indexOf(index) !== -1) {
    return window.REGION_HREF.RU;
  }
  if (window.REGION_IN.indexOf(index) !== -1) {
    return window.REGION_HREF.IN;
  }
};

const SAToUSA = (index) => {
  if (window.REGION_SA.indexOf(parseInt(index, 10)) !== -1) {
    return 23;
  }
  return index;
};
const parseDate = (timestamp, hasTime = false, formType = false) => {
  if (timestamp >= 4102329600000) {
    return i18n.t('perpetual');
  }
  if (!timestamp) {
    timestamp = new Date().getTime();
  }
  if (typeof timestamp === 'string') {
    if ((/^\d+$/).test(timestamp)) {
      timestamp = +timestamp;
    }
  }
  //对于少数“月份”名称显示较长(大部分月份名称超过4个字符)的语言(如越南语、阿拉伯语、爱沙尼亚语、乌克兰语等)，对应的月份多语言翻译改为数字。
  let month = i18n.t(`month_${new Date(timestamp).getMonth() + 1}`);
  if (['vi', 'ar', 'es', 'uk'].includes(i18n.locale)) {
    month = String(new Date(timestamp).getMonth() + 1).padStart(2, 0);
  }
  if (hasTime) {
    return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()} ${moment(timestamp).format(
      'HH:mm:ss'
    )}`;
  }
  if (!hasTime && formType) {
    return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()} ${moment(timestamp).format(
      'HH:mm:ss'
    )}`;
  }
  return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()}`;
};
const parseTime = (timestamp, hasTime = false, formType = false) => {
  if (!timestamp) {
    timestamp = new Date().getTime();
  }
  let month = i18n.t(`month_${new Date(timestamp).getMonth() + 1}`);
  if (['vi', 'ar', 'es', 'uk'].includes(i18n.locale)) {
    month = String(new Date(timestamp).getMonth() + 1).padStart(2, 0);
  }
  if (hasTime) {
    return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()} ${moment(timestamp).format(
      'HH:mm'
    )}`;
  }
  if (!hasTime && formType) {
    return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()} ${moment(timestamp).format(
      'HH:mm'
    )}`;
  }
  return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()}  ${new Date(
    timestamp
  ).getHours()} : ${new Date(timestamp).getMinutes()}`;
};
const parseDateMonth = (timestamp, hasTime = false, formType = false) => {
  if (!timestamp) {
    timestamp = new Date().getTime();
  }
  let month = i18n.t(`month_${new Date(timestamp).getMonth() + 1}`);
  if (['vi', 'ar', 'es', 'uk'].includes(i18n.locale)) {
    month = String(new Date(timestamp).getMonth() + 1).padStart(2, 0);
  }
  if (hasTime) {
    return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()} ${moment(timestamp).format(
      'HH:mm:ss'
    )}`;
  }
  if (!hasTime && formType) {
    return `${new Date(timestamp).getDate()}/${month}/${new Date(timestamp).getFullYear()} ${moment(timestamp).format(
      'HH:mm:ss'
    )}`;
  }
  return `${month}/ ${new Date(timestamp).getFullYear()}`;
};

/**
 * 压缩图片
 * @param file input获取到的文件
 */
const compressFile = (file) => {
  return new Promise((resolve) => {
    if (file) {
      let fileObj = file;
      let reader = new FileReader();
      reader.readAsDataURL(fileObj); //转base64
      reader.onload = function (e) {
        let image = new Image(); //新建一个img标签（还没嵌入DOM节点)
        image.src = e.target.result;
        image.onload = function () {
          if (image.width > 1000 || image.height > 1000) {
            let canvas = document.createElement('canvas'); // 新建canvas
            let context = canvas.getContext('2d');
            let imageWidth;
            let imageHeight;
            let data;
            if (image.width > image.height) {
              imageWidth = 1000;
              imageHeight = (1000 / image.width) * image.height;
            } else {
              imageHeight = 1000;
              imageWidth = (1000 / image.height) * image.width;
            }
            canvas.width = imageWidth;
            canvas.height = imageHeight;
            context.drawImage(image, 0, 0, imageWidth, imageHeight);
            data = canvas.toDataURL(file.type); // 输出压缩后的base64
            let arr = data.split(',');
            let mime = arr[0].match(/:(.*?);/)[1]; // 转成blob
            let bstr = atob(arr[1]);
            let n = bstr.length;
            let u8arr = new Uint8Array(n);
            while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }
            let files = new window.File([new Blob([u8arr], { type: mime })], file.name, { type: file.type }); // 转成file
            resolve(files); // 回调
          } else {
            resolve(file);
          }
        };
      };
    } else {
      resolve(file);
    }
  });
};

const syncResponse = (target, name, descriptor) => {
  const method = descriptor.value;
  let pending = Object.create(null);
  descriptor.value = function (...args) {
    if (pending && pending.cancel) {
      pending.cancel();
    }

    return Promise.race([
      method.apply(this, args),
      new Promise((_, reject) => {
        pending.cancel = function () {
          // 此处不使用resolve(null) 减少table不必要渲染  reject 会触发  hui h-paged-table 的fetch-error事件
          reject();
        };
      })
    ])
      .then((v) => {
        return v;
      })
      .finally(() => {
        pending = Object.create(null);
      });
  };
  return descriptor;
};

const filterCurrentCountryDisplayList = (allList, countryListKey, country) => {
  //根据黑白名单筛选当前国家能够显示的教学视频/帮助文档
  return allList.filter((list) => {
    let countryList = list[countryListKey];
    if (0 === countryList[0]) {
      //countryList[0]为0表示该项对所以国家开放
      return true;
    } else if (
      //countryList[0]为1表示该项仅对countryList[1]列表中的国家开放
      1 === countryList[0]
      && countryList[1].includes(country)
    ) {
      return true;
    } else if (
      //countryList[0]为-1表示该项对除countryList[1]列表中的国家之外的国家开放
      -1 === countryList[0]
      && !countryList[1].includes(country)
    ) {
      return true;
    }
    return false;
  });
};

class PollingTask {
  /**
   * @param {Promise} task 轮询任务
   * @param {function stopCallBack(isWoring) {
   *  isWoring: task是否正在运行
   * }}
   */
  constructor(task) {
    // 任务ID
    this.taskId = null;
    // 轮询间隔
    this.pollingInterval = 60 * 1000;
    // 轮询task
    this.task = task;
    // 是否正在执行中
    this.isWorking = false;
    // 运行task产生的Promise对象
    this._taskPromise = null;
    // 是否停止轮询
    this._isStop = false;
  }

  // 开始轮询
  start() {
    // console.log('starting...')
    if (!this._isStop) {
      this.taskId = setTimeout(async () => {
        this.isWorking = true;
        // console.log('working start...')
        this._taskPromise = this.task();
        this._taskPromise.finally(() => {
          clearTimeout(this.taskId);
          this.isWorking = false;
          // console.log('working end...')
          this.start();
        });
      }, this.pollingInterval);
    } else {
      this._isStop = false;
      // console.log('ending...')
    }
  }
  // 结束轮询
  stop() {
    // task还未开始执行，清除定时器任务以阻止继续执行
    if (this.taskId && !this.isWorking) {
      clearTimeout(this.taskId);
      // console.log('stop immediately')
      return Promise.resolve(true);
    }
    // task在执行中，设置停止轮询标识，阻止下次执行
    if (this.taskId && this.isWorking) {
      this._isStop = true;
      // console.log('stop next')
      return new Promise((resolve) => {
        this._taskPromise.finally(() => {
          // console.log('current work end');
          resolve(true);
        });
      });
    }
    return Promise.resolve(true);
  }
}
const calcStrLen = (str) => {
  let len = 0;
  for (let i = 0; i < str.length; i++) {
    if (str.charCodeAt(i) > 0 && str.charCodeAt(i) < 128) {
      len++;
    } else {
      len += 2;
    }
  }
  return len;
};
const fittingString = (str, maxWidth, fontSize) => {
  const fontWidth = fontSize * 1.3; //字号+边距
  maxWidth = maxWidth * 2;
  let inputLength = calcStrLen(str);
  const actualLen = Math.floor((maxWidth - 10) / fontWidth);
  const ellipsis = '…';
  if (inputLength > actualLen) {
    let newStr = str.slice(0, -1);
    while (inputLength > actualLen) {
      newStr = newStr.slice(0, -1);
      inputLength = calcStrLen(newStr);
    }
    return newStr + ellipsis;
  }
  return str;
};

/**
 * 设备版本比较
 *
 * @param {string} szVersion [IN]：设备版本
 * @param {string} szCompareVersion [IN]：设备对比版本
 *
 * @returns {number} 对比结果 大于:1 等于:0 小于:-1
 */
const versionCompare = (szVersion, szCompareVersion) => {
  let aVersion = [];
  let aCompareVersion = [];
  let bVersion = -1;

  if (szVersion !== '') {
    szVersion = szVersion.replace(/v/gi, '');
    aVersion = szVersion.split('.');
  } else {
    return bVersion;
  }
  if (szCompareVersion !== '') {
    szCompareVersion = szCompareVersion.replace(/v/gi, '');
    aCompareVersion = szCompareVersion.split('.');
  } else {
    return bVersion;
  }

  if (aVersion.length === aCompareVersion.length) {
    for (let i = 0, iLen = aVersion.length; i < iLen; i++) {
      if (parseInt(aVersion[i], 10) === parseInt(aCompareVersion[i], 10)) {
        bVersion = 0;
        continue;
      } else if (parseInt(aVersion[i], 10) > parseInt(aCompareVersion[i], 10)) {
        bVersion = 1;
        break;
      } else {
        bVersion = -1;
        break;
      }
    }
  }

  return bVersion;
};

// webControl参数转换
/**
 * 调整透传参数
 *
 * @param {object} oInfo [IN]：token对象
 * @param {object} szUrl [IN]：请求url
 * @param {object} szSerial [IN]：设备序列号
 *
 * @returns {object} 返回新参数
 */
const changeProxyObj = (oInfo, szUrl, szSerial) => {
  let oNewParam = {
    url: szUrl,
    serial: szSerial
  };

  if (oInfo[szSerial]) {
    oNewParam.serial = oInfo[szSerial].boxDeviceSerial;

    if (szUrl.indexOf('?') > -1) {
      oNewParam.url = `${szUrl}&devIndex=${oInfo[szSerial].deviceIndex}`;
    } else {
      oNewParam.url = `${szUrl}?devIndex=${oInfo[szSerial].deviceIndex}`;
    }
  }

  return oNewParam;
};

// 获取给设备配套用的多语言key值
const getLngForDevice = () => {
  // 除巴西葡语外，其余key值均相同
  if (i18n.locale === 'pt_BR') {
    return 'pt-br';
  }
  return i18n.locale;
};

// 直接设置对应语言给设备配套使用
const setLngForDevice = () => {
  sessionStorage.setItem(
    'deviceInfo',
    JSON.stringify({
      language: getLngForDevice()
    })
  );
};

// 获取短序列号
const getSerialNo = (SerialNumber) => {
  //长序列号处理为短序列号
  let serialNo = SerialNumber;
  let longSerialReg = /^[\s\S]*([\s\S]{8}\d)[^\d]*$/;
  if (serialNo.match(longSerialReg)) {
    return serialNo.match(longSerialReg)[1];
  }
  return serialNo;
};

/**
 * 跟距cookie隐私管理删除Analytics Cookies
 */
// const getCookieManager = () => {
//   let cookieManager = storage.getObj(storage.NAME.COOKIE_DESCRIBE);
//   if (cookieManager && !cookieManager.analyCookie) {
//     window.gtag = null;
//     let cookieList = document.cookie && document.cookie.split(';');
//     for (let i in cookieList) {
//       let item = cookieList[i];
//       let key = item && item.split('=')[0].trim();
//       key.includes('_ga') || key.includes('_gid') ? cookie.remove(key) : '';
//     }
//   } else {
//     if (!window.HAS_QUERY_GOOGLE_ANALYTICS) {
//       // 若BURIED_POINT_IDENTITY不存在 说明init函数从未执行 启用Analytics cookie 执行init函数
//       window['ga-disable-' + window.BURIED_POINT_IDENTITY] = false;
//       initGA();
//     }
//   }
// };

const timestamp2str = (lastOfflineTime, networkStatus = 0, offlineTime) => {
  if (lastOfflineTime === -1 && typeof offlineTime === 'undefined') {
    return '- -';
  }
  if (parseInt(networkStatus, 10) === 1) {
    return '- -';
  }
  let hasBigger = false;
  if (lastOfflineTime < 0) {
    hasBigger = true;
  }
  // 不知道上方的意图是什么，但是offlineTime未识别时，return掉，防止出现NaN
  if (typeof offlineTime === 'undefined') {
    return '- -';
  }
  const t = Math.abs(offlineTime);
  const days = Math.floor(t / 1000 / 60 / 60 / 24);
  const hours = Math.floor((t / 1000 / 60 / 60) % 24);
  const minutes = Math.floor((t / 1000 / 60) % 60);
  const second = Math.floor((t / 1000) % 60);
  if (days === 0) {
    if (hasBigger) {
      return `> ${i18n.t('time_format_2').replace('{1}', 0)}`;
    }
    return i18n.t('time_format_1').replace('{1}', hours).replace('{2}', minutes).replace('{3}', second);
  }
  if (hasBigger) {
    return `> ${i18n.t('time_format_2').replace('{1}', days)}`;
  }
  return i18n.t('time_format').replace('{1}', days).replace('{2}', hours).replace('{3}', minutes);
};
const timestamp2strForHealthCheckTable = (row) => {
  return timestamp2str(row.lastOfflineTime || -1, row.networkStatus, row.offlineTime);
};

const origin = window.location.origin;
const reg = {
  uat: {
    ep: /^https:\/\/hpc-sgp-uat-(\d+)\.([\d\w-]+\..+)/,
    epSupport: /^https:\/\/uat(\d+)-support\.([\d\w-]+\..+)/
  },
  prod: {
    ep: /^https:\/\/((?=\w+\.[\d\w-]+\.)\w+)?\.?([\d\w-]+\..+)/,
    epSupport: /^https:\/\/(\w+)?-?support\.([\d\w-]+\..+)/ //https://ius-support.***.***|https://support.***.***
  }
};

const supportFn = {
  getHpcOrigin() {
    const uatMatch = origin.match(reg.uat.epSupport);
    if (uatMatch) {
      return `https://hpc-sgp-uat-${uatMatch[1]}.${uatMatch[2]}`;
    }
    const prodMatch = origin.match(reg.prod.epSupport);
    if (prodMatch) {
      return `https://${prodMatch[1] || 'www'}.${prodMatch[2]}`;
    }
  },
  getSupportOrigin() {
    const uatMatch = origin.match(reg.uat.ep);
    if (uatMatch) {
      return `https://uat${uatMatch[1]}-support.${uatMatch[2]}`;
    }
    const prodMatch = origin.match(reg.prod.ep);
    if (prodMatch) {
      let pre = '';
      if (prodMatch[1] !== 'www' && typeof prodMatch[1] !== 'undefined') {
        pre = `${prodMatch[1]}-`;
      }
      return `https://${pre}support.${prodMatch[2]}`;
    }
  }
};

/**
 * 高亮文本中匹配的关键字 使用v-html插入需要处理的文本，不能直接使用{{text}}
 * @param {*} text 文本
 * @param {*} key 关键词
 */
const hightlightKeywords = (text, key) => {
  if (!key) {
    return text;
  }
  const specialCharts = ['(', ')', "'", '\\', '$', '*', '+', '[', ']', '?', '^', '{', '}', '|', '.'];
  let wordStr = '';
  for (let i = 0, len = key.length; i < len; i++) {
    if (specialCharts.includes(key[i])) {
      wordStr += `\\${key[i]}`;
    } else {
      wordStr += key[i];
    }
  }
  const wordReg = new RegExp(wordStr, 'ig');
  const keyWordArr = text.match(wordReg);

  const vNodeArr = text.split(wordReg).reduce((all, item, index, arr) => {
    item && all.push(item);
    if (index !== arr.length - 1) {
      all.push(`<span style="color: #E81123">${keyWordArr.shift()}</span>`);
    }
    return all;
  }, []);
  return vNodeArr.join('');
};
// 浏览器、版本等
const parseBrowser = () => {
  const result = Bowser.getParser(navigator.userAgent);
  const parsed = result.getResult();
  return parsed;
};
// 获取设备指纹，若没有则生成设备指纹并存储(笔记本上发现不同浏览器的指纹是一样的，需要补充浏览器信息)
const handleFingerPrint = async (type, id) => {
  try {
    if (type === 'generate') {
      const currentFGFlag = Storage.realGetByUser('fingerprint', id);
      if (currentFGFlag) {
        return currentFGFlag;
      }
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      console.log(`output->result`, result, result.components.audio.value);
      console.log(`output->bowser`, parseBrowser()?.browser);
      const browser = parseBrowser()?.browser;
      const fingerprint = `${result.visitorId}-${browser?.name}-${browser?.version}`;
      if (id) {
        Storage.realSetByUser('fingerprint', fingerprint, id);
      }
      return fingerprint; // 返回唯一哈希值
    }
    return null; // 或者根据业务需求返回适当的值
  } catch (error) {
    console.warn('获取设备指纹失败:', error);
    return null;
  }
};


export {
  basicCheck,
  getPasswordStrength,
  parseXmlFromFilePath,
  formatPhoneNum,
  formatCompanyPhoneNum,
  base64Util,
  joinPhoneNum,
  getJson,
  capitalize,
  copyToClipboard,
  removeDom,
  updatePermission,
  updatePermissionName,
  splitPermission,
  createDom,
  getSameLevelMaxZindex,
  formatMoneyNumber,
  getQueryString,
  getURLByCountryIndex,
  parseDate,
  parseDateMonth,
  SAToUSA,
  compressFile,
  syncResponse,
  filterCurrentCountryDisplayList,
  PollingTask,
  fittingString,
  getPhoneNum,
  getPhoneCountry,
  getLngForDevice,
  setLngForDevice,
  versionCompare,
  changeProxyObj,
  getSerialNo,
  timestamp2str,
  timestamp2strForHealthCheckTable,
  parseTime,
  supportFn,
  transferToKey,
  getCookiesOption,
  getPasswordStrongStrength,
  hightlightKeywords,
  parseBrowser,
  handleFingerPrint
};
